import { getFirstNameLastInitial } from '@bybeam/formatting';
import { CaseStatus, User, WorkflowAction, WorkflowEvent } from '@bybeam/platform-types';
import { formatDate } from '@platform-ui-common/utilities/format.js';
import { formatCaseStatus } from '@platform-ui-common/utilities/statuses.js';

function formatWorkflowEventValue(key: string) {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function formatAction(action: WorkflowAction) {
  switch (action) {
    case WorkflowAction.CaseComment:
      return 'New message sent';
    default:
      return formatWorkflowEventValue(action);
  }
}

function formatValue(action: WorkflowAction, value: string) {
  switch (action) {
    case WorkflowAction.ChangeStatus:
      return formatCaseStatus(value as CaseStatus);
    default:
      return formatWorkflowEventValue(value);
  }
}

function replaceWithBr(text: string) {
  return text.replace(/\\n/g, '<br />');
}

function getAuthorName(user: User) {
  let author = '';
  if (user?.name) author = getFirstNameLastInitial(user.name);
  return author || user?.email || 'Unknown user';
}

function formatAuthor(action: WorkflowAction, user: User) {
  switch (action) {
    case WorkflowAction.CaseComment:
    case WorkflowAction.Submission:
      return getAuthorName(user);
    default:
      return 'Case manager';
  }
}

function formatPreposition(action: WorkflowAction) {
  switch (action) {
    case WorkflowAction.AppliedAutomatedTag:
    case WorkflowAction.RemovedTag:
    case WorkflowAction.AppliedTag:
      return ':';
    default:
      return 'to';
  }
}

const ActivityFeed: React.FC<{ workflowEvents: WorkflowEvent[] }> = ({ workflowEvents }) => {
  return (
    <ul className="flex flex-col gap-0.5">
      {workflowEvents?.length ? (
        workflowEvents.map(({ user, id, action, details, createdAt, previousValue, newValue }) => {
          return (
            <li key={id} className="group text-textSecondary relative">
              <div className="absolute h-full left-0 top-[12px] bottom-[8px] flex flex-col ">
                <div className="w-0.5 bg-overlay rounded ml-0.5 mt-[7px] flex-1 group-last-of-type:hidden" />
              </div>
              <div className="flex gap-2.5 items-center mb-1">
                <div className="h-1.5 w-1.5 rounded bg-overlay mt-1" />
                <div>
                  [{formatAuthor(action, user)}] <strong>{formatAction(action)}</strong>{' '}
                  {action !== WorkflowAction.Reassign && (
                    <>
                      {!!previousValue && (
                        <>
                          from <strong>{formatValue(action, previousValue)}</strong>{' '}
                        </>
                      )}
                      {!!newValue && action !== WorkflowAction.Submission && (
                        <>
                          {formatPreposition(action)}{' '}
                          <strong>{formatValue(action, newValue)}</strong>{' '}
                        </>
                      )}
                    </>
                  )}
                  <span>on {formatDate(createdAt)}</span>
                </div>
              </div>
              {details ? (
                <span className="border rounded border-tableBorder inline-flex items-center p-2 mb-2 ml-4">
                  <small
                    className="whitespace-pre-wrap break-all"
                    // biome-ignore lint/security/noDangerouslySetInnerHtml: details are sanitized before going into workflow events table
                    dangerouslySetInnerHTML={{ __html: replaceWithBr(details) }}
                  />
                </span>
              ) : null}
            </li>
          );
        })
      ) : (
        <li>No activity found</li>
      )}
    </ul>
  );
};

export default ActivityFeed;
